import React from 'react'
import styled from '@emotion/styled'

import mq from '../styles/breakpoints'

import { Heading, Paragraph } from '../components/typography'
import { Image } from '../components/Image'
import { Section } from '../components/Section'
import { IStatistics } from '../graphql/sections/statistics'
import { ICopy } from '../graphql/modules/copy'

interface IProps {
  data: IStatistics
}
const customSectionStyles = {
  padding: ['35px 41px', '50px 36px', '50px 163px'],
}

// Tablet and desktop: one equal-width column per statistic, so a longer caption (e.g. in French) wraps
// inside its column instead of pushing the others aside. Each statistic spans three shared rows (image,
// title, caption) through a subgrid, so the captions start on the same line whatever the image heights.
// Browsers without subgrid stack each statistic as a plain column.
const StatisticsContainer = styled.div`
  ${mq({
    display: ['flex', 'grid', 'grid'],
    flexDirection: 'column',
    gridAutoFlow: 'column',
    gridAutoColumns: '1fr',
    gridTemplateRows: 'auto auto auto',
    columnGap: ['0', '24px', '24px'],
    marginTop: ['25px', '25px', '50px'],
    width: '100%',
  })}
`

const Statistic = styled.div`
  ${mq({
    display: ['flex', 'grid', 'grid'],
    flexDirection: 'column',
    // Mobile: centred in the column. Grid: top of each row, so the captions start on the same line.
    alignItems: ['center', 'start', 'start'],
    gridRow: 'span 3',
    gridTemplateRows: 'subgrid',
    justifyItems: 'center',
  })}
`

// The images have different aspect ratios: bottom-aligned in the shared row, so they sit on the same line.
const ImageArea = styled.div`
  ${mq({
    display: 'flex',
    justifyContent: 'center',
    gridRow: 1,
    alignSelf: ['auto', 'end', 'end'],
  })}
`

const imageStyles = {
  width: ['190px', '190px', '240px'],
  height: 'inherit',
  margin: '0',
}

const statisticTitleStyles = { margin: ['0', '15px 0', '15px 0'], textAlign: 'center', gridRow: 2 }

const paragraphStyles = {
  margin: ['0 0 30px 0', '0 0 50px 0', ''],
  textAlign: 'center',
  gridRow: 3,
}

const Statistics: React.FC<{ data: ICopy[] }> = ({ data }) => {
  return (
    <>
      {data.map(({ image, text, title }, index) => {
        const altText = image?.alternateText.alternateText ?? ''
        const fluidImg = image?.image.gatsbyImageData
        const statisticText = text?.text

        return (
          <Statistic key={index}>
            <ImageArea>
              <Image fluid={fluidImg} alt={altText} customStyles={imageStyles} />
            </ImageArea>
            <Heading as="h2" styleOverrides={statisticTitleStyles}>
              {title}
            </Heading>
            <Paragraph styleOverrides={paragraphStyles}>{statisticText}</Paragraph>
          </Statistic>
        )
      })}
    </>
  )
}

export const StatisticsSection: React.FC<IProps> = ({ data: { title, statistics } }) => {
  return (
    <Section title={title} customStyles={customSectionStyles}>
      <Heading as="h2">{title}</Heading>
      <StatisticsContainer>
        <Statistics data={statistics} />
      </StatisticsContainer>
    </Section>
  )
}
